import type { ClientType } from "../../../shared/hooks/use-client-type";
import type { ApplicationDetail } from "../application-detail-page.lib";

export type TransitionAction =
  | "intake-pass"
  | "request-revision"
  | "reject"
  | "take-ng"
  | "send-to-ntp"
  | "take-ntp"
  | "assign-ntp-resp"
  | "start-commission-prep"
  | "start-decision"
  | "submit-ntp-approval"
  | "ntp-rework"
  | "ntp-approve"
  | "ng-rework"
  | "ng-approve"
  | "rework-resubmit";

/** UI-only actions that open a modal or call a non-transition API. */
export type ApplicationUiAction = TransitionAction | "redirect-ng" | "redirect-ntp" | "assign-region-responsible" | "reassign-ntp-responsible";

export type ApplicationControlButtonVariant = "take-work" | "success" | "attention" | "error" | "info";

export type ApplicationControlDefinition =
  | { type: "export"; disabled?: boolean }
  | { type: "import"; disabled?: boolean }
  | { type: "submit-revision" }
  | { type: "notify-payment" }
  | {
      type: "action";
      action: ApplicationUiAction;
      label: string;
      variant: ApplicationControlButtonVariant;
      requiresModal?: boolean;
      disabled?: boolean;
    };

type UserRoles = ReturnType<typeof import("../../../shared/hooks/use-user-roles").useUserRoles>;

const actionControl = (
  action: ApplicationUiAction,
  label: string,
  variant: ApplicationControlButtonVariant,
  requiresModal = false,
  disabled = false,
): ApplicationControlDefinition => ({
  type: "action",
  action,
  label,
  variant,
  requiresModal,
  disabled,
});

const isOperatorRole = (clientType: ClientType, roles: UserRoles) =>
  (clientType === "external" && roles.isExternalOperator) || (clientType === "internal" && roles.isInternalOperator);

const isInternalNgSecretaryRole = (clientType: ClientType, roles: UserRoles) => clientType === "internal" && roles.isInternalNgSecretary;

const isInternalApparatusNgRole = (clientType: ClientType, roles: UserRoles) => clientType === "internal" && roles.isInternalApparatusNg;

const isInternalNtpChiefRole = (clientType: ClientType, roles: UserRoles) => clientType === "internal" && roles.isInternalNtpChief;

const isInternalNtpResponsibleRole = (clientType: ClientType, roles: UserRoles) => clientType === "internal" && roles.isInternalNtpResponsible;

const getOperatorControls = (application: ApplicationDetail): ApplicationControlDefinition[] => {
  const needsExport = application.processingFlag === "NEEDS_EXPORT";

  return [
    { type: "export", disabled: !needsExport },
    { type: "import", disabled: needsExport },
  ];
};

const getExternalApplicantControls = (application: ApplicationDetail): ApplicationControlDefinition[] => {
  if (application.status === "REVISION_REQUIRED") {
    return [{ type: "submit-revision" }];
  }

  if (application.status === "WAITING_PAYMENT") {
    return [{ type: "notify-payment" }];
  }

  return [];
};

const getInternalNgSecretaryControls = (application: ApplicationDetail): ApplicationControlDefinition[] => {
  switch (application.status) {
    case "INTAKE_CHECK":
      return [
        actionControl("request-revision", "Вернуть на доработку", "attention", true),
        actionControl("intake-pass", "Направить в аппарат НГ", "success", true),
      ];
    case "SENT_TO_NG":
      return [actionControl("redirect-ng", "Перенаправить", "info", true)];
    default:
      return [];
  }
};

const getInternalApparatusNgControls = (application: ApplicationDetail, canApprove = true): ApplicationControlDefinition[] => {
  switch (application.status) {
    case "SENT_TO_NG":
      return [actionControl("take-ng", "Взять в работу", "take-work")];
    case "SENT_TO_NTP":
      return [actionControl("redirect-ntp", "Перенаправить", "info", true)];
    case "IN_NG":
      return [
        actionControl("send-to-ntp", "Направить в НТП", "success", true),
        actionControl("request-revision", "Вернуть на доработку", "attention", true),
        actionControl("reject", "Оформить отказ", "error", true),
      ];
    case "NG_APPROVAL":
      return [
        actionControl("ng-approve", "Согласовать", "success", true, !canApprove),
        actionControl("ng-rework", "Вернуть на доработку", "attention", true),
        actionControl("reject", "Оформить отказ", "error", true),
      ];
    default:
      return [];
  }
};

const getInternalNtpChiefControls = (application: ApplicationDetail): ApplicationControlDefinition[] => {
  switch (application.status) {
    case "SENT_TO_NTP":
      return [actionControl("take-ntp", "Взять в работу", "take-work")];
    case "IN_NTP":
      return [
        actionControl("assign-ntp-resp", "Назначить ответственного в НТП", "success", true),
        actionControl("assign-region-responsible", "Назначить ответственных в регионе", "success", true),
      ];
    case "NTP_RESP_ASSIGNED":
      return [
        actionControl("reassign-ntp-responsible", "Переназначить ответственного НТП", "info", true),
        actionControl("assign-region-responsible", "Назначить ответственных в регионе", "success", true),
      ];
    case "NTP_APPROVAL":
      return [actionControl("ntp-approve", "Согласовать", "success"), actionControl("ntp-rework", "Вернуть на доработку", "attention", true)];
    default:
      return [];
  }
};

const getInternalNtpResponsibleControls = (application: ApplicationDetail): ApplicationControlDefinition[] => {
  switch (application.status) {
    case "NTP_RESP_ASSIGNED":
      return [actionControl("start-commission-prep", "Взять в работу", "take-work")];
    case "COMMISSION_PREP":
      return [
        actionControl("assign-region-responsible", "Назначить ответственных в регионе", "success", true),
        actionControl("start-decision", "Комиссия проведена", "success"),
      ];
    case "DECISION_FORMING":
      // Отправить можно только при сформированном черновике итогового решения.
      return [actionControl("submit-ntp-approval", "Отправить на согласование", "success", false, !application.finalDecision.isDraft)];
    case "DECISION_REWORK":
      // Повторная отправка доступна только после создания нового черновика (версии).
      return [actionControl("rework-resubmit", "Отправить на согласование", "success", false, !application.finalDecision.isDraft)];
    default:
      return [];
  }
};

export const getApplicationControlDefinitions = (
  application: ApplicationDetail,
  clientType: ClientType | null,
  roles: UserRoles,
  canApprove = true,
): ApplicationControlDefinition[] => {
  if (!clientType) {
    return [];
  }

  if (isOperatorRole(clientType, roles)) {
    return getOperatorControls(application);
  }

  if (clientType === "external" && roles.isExternalApplicant) {
    return getExternalApplicantControls(application);
  }

  if (isInternalNgSecretaryRole(clientType, roles)) {
    return getInternalNgSecretaryControls(application);
  }

  if (isInternalApparatusNgRole(clientType, roles)) {
    return getInternalApparatusNgControls(application, canApprove);
  }

  if (isInternalNtpChiefRole(clientType, roles)) {
    return getInternalNtpChiefControls(application);
  }

  if (isInternalNtpResponsibleRole(clientType, roles)) {
    return getInternalNtpResponsibleControls(application);
  }

  return [];
};

export const isTransitionAction = (action: ApplicationUiAction): action is TransitionAction =>
  action !== "redirect-ng" && action !== "redirect-ntp" && action !== "assign-region-responsible" && action !== "reassign-ntp-responsible";

/**
 * Доступ к управлению ответственными подразделениями региона по ТЗ STORY-998:
 * - НТП при статусах «В работе у НТП» (IN_NTP) и «Назначен ответственный НТП» (NTP_RESP_ASSIGNED);
 * - Ответственный НТП при статусе «Подготовка комиссии» (COMMISSION_PREP).
 */
export const canManageRegionDepartments = (
  application: Pick<ApplicationDetail, "status">,
  clientType: ClientType | null,
  roles: UserRoles,
): boolean => {
  if (clientType !== "internal") {
    return false;
  }

  const isNtpChiefEligible = roles.isInternalNtpChief && (application.status === "IN_NTP" || application.status === "NTP_RESP_ASSIGNED");
  const isNtpResponsibleEligible = roles.isInternalNtpResponsible && application.status === "COMMISSION_PREP";

  return isNtpChiefEligible || isNtpResponsibleEligible;
};
